import { createRoot } from "react-dom/client";
import type { ValidatedInvoice } from "@/core/pharmacy/types";
import { formatInvoiceDate, formatMoney } from "@/core/pharmacy/format";

interface InvoicePrintSheetProps {
  invoice: ValidatedInvoice;
}

export function InvoicePrintSheet({ invoice }: InvoicePrintSheetProps) {
  const validatedLabel = formatInvoiceDate(invoice.validatedAt);

  return (
    <div className="ph-invoice-print-sheet">
      <div className="ph-invoice-header">
        <div>
          <div className="ph-invoice-brand">ITaaS Hospitol</div>
          <div className="ph-invoice-sub">Module pharmacie — facture officine</div>
        </div>
        <div className="ph-invoice-meta text-end">
          <div className="ph-invoice-title">FACTURE</div>
          <div className="fw-semibold">{invoice.number}</div>
          <div className="small text-muted">{validatedLabel}</div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Client</div>
          <div className="fw-semibold">{invoice.customer.name}</div>
          {invoice.customer.phone && (
            <div className="small text-muted">Tél. {invoice.customer.phone}</div>
          )}
          {invoice.customer.address && (
            <div className="small text-muted">{invoice.customer.address}</div>
          )}
        </div>
        <div className="col-md-6 text-md-end">
          <div className="ph-invoice-section-title">Paiement</div>
          <div className="badge bg-success">Payée</div>
        </div>
      </div>

      <table className="table table-sm ph-invoice-table">
        <thead>
          <tr>
            <th>Produit</th>
            <th>Code</th>
            <th className="text-end">Prix unit.</th>
            <th className="text-end">Qté</th>
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.id}>
              <td>{line.label}</td>
              <td className="small text-muted">{line.barcode ?? "—"}</td>
              <td className="text-end">{formatMoney(line.unitPrice)}</td>
              <td className="text-end">{line.quantity}</td>
              <td className="text-end fw-semibold">
                {formatMoney(line.unitPrice * line.quantity)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ph-invoice-footer">
        <div className="small text-muted">
          Document généré par ITaaS Hospitol — merci de votre confiance.
        </div>
        <div className="ph-invoice-totals">
          <div className="d-flex justify-content-between">
            <span>Sous-total</span>
            <strong>{formatMoney(invoice.subtotal)}</strong>
          </div>
          <div className="d-flex justify-content-between ph-invoice-grand">
            <span>Total payé</span>
            <strong>{formatMoney(invoice.subtotal)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

export function printInvoice(invoice: ValidatedInvoice): void {
  const portal = document.createElement("div");
  portal.className = "ph-print-portal";
  portal.setAttribute("aria-hidden", "true");
  document.body.appendChild(portal);

  const root = createRoot(portal);
  root.render(<InvoicePrintSheet invoice={invoice} />);

  window.setTimeout(() => {
    window.print();
    root.unmount();
    portal.remove();
  }, 150);
}
